import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	LOCAL_AUTO_MIN_RMS,
	LOCAL_MAX_RMS,
	SPEAKING_LOCAL_RELEASE_MS,
} from '@app/features/voice/engine/VoiceSpeakingThreshold';
import {detectWasmSimdSupport} from '@app/features/voice/utils/noise_suppression/NoiseSuppressionBackends';
import {VOICE_PIPELINE_TUNING, type VoicePipelineTuning} from '@app/features/voice/utils/VoicePipelineTuning';

const GATE_STARTUP_TIMEOUT_MS = 8000;
const modules = new WeakMap<AudioContext, Promise<void>>();
const vadModules = new WeakMap<AudioContext, Promise<void>>();
const gainModules = new WeakMap<AudioContext, Promise<void>>();
const logger = new Logger('VoiceActivityGate');

export interface VoiceActivityGateConfig {
	apply: boolean;
	auto: boolean;
	manualThresholdRms: number;
	pipelineV2Enabled?: boolean;
	tuning?: VoicePipelineTuning;
}

export interface VoiceInputLevel {
	rms: number;
	floorRms: number;
	thresholdRms: number;
	speaking: boolean;
	nonFinite: boolean;
	frame: number;
	contextTime: number;
	processingMs?: number;
	audioMs?: number;
}

function withStartupDeadline<T>(pending: Promise<T>, signal: AbortSignal, message: string): Promise<T> {
	return new Promise((resolve, reject) => {
		const finish = (error: unknown, value?: T): void => {
			clearTimeout(timer);
			signal.removeEventListener('abort', onAbort);
			if (error === undefined) resolve(value as T);
			else reject(error);
		};
		const onAbort = (): void => finish(signal.reason);
		const timer = setTimeout(() => finish(new Error(message)), GATE_STARTUP_TIMEOUT_MS);
		signal.addEventListener('abort', onAbort, {once: true});
		if (signal.aborted) onAbort();
		void pending.then((value) => finish(undefined, value), finish);
	});
}

function loadWorklet(
	context: AudioContext,
	signal: AbortSignal,
	cache: WeakMap<AudioContext, Promise<void>>,
	asset: 'VOICE_GATE_WORKLET_URL' | 'VOICE_VAD_WORKLET_URL' | 'VOICE_SOFT_GAIN_WORKLET_URL',
): Promise<void> {
	signal.throwIfAborted();
	let pending = cache.get(context);
	if (!pending) {
		pending = import('@app/features/voice/utils/noise_suppression/NoiseSuppressionWorkletAssets').then((assets) =>
			context.audioWorklet.addModule(assets[asset]),
		);
		cache.set(context, pending);
		void pending.catch(() => {
			if (cache.get(context) === pending) cache.delete(context);
		});
	}
	return withStartupDeadline(pending, signal, 'Voice activity gate did not load in time');
}

const WASM_MAGIC = [0x00, 0x61, 0x73, 0x6d];

async function fetchSpeechModel(): Promise<ArrayBuffer> {
	const {VOICE_VAD_SIMD_WASM_URL} = await import(
		'@app/features/voice/utils/noise_suppression/NoiseSuppressionWorkletAssets'
	);
	const response = await fetch(VOICE_VAD_SIMD_WASM_URL, {credentials: 'same-origin'});
	if (!response.ok) throw new Error(`Speech detector request failed with status ${response.status}`);
	const bytes = await response.arrayBuffer();
	const head = new Uint8Array(bytes, 0, Math.min(4, bytes.byteLength));
	if (!WASM_MAGIC.every((value, index) => head[index] === value)) throw new Error('Speech detector is not WebAssembly');
	return bytes;
}

export async function loadVoiceActivityGate(
	context: AudioContext,
	signal: AbortSignal,
	pipelineV2Enabled = false,
): Promise<ArrayBuffer | null> {
	await loadWorklet(context, signal, modules, 'VOICE_GATE_WORKLET_URL');
	if (!pipelineV2Enabled || context.sampleRate !== 48000) return null;
	if (detectWasmSimdSupport()) {
		try {
			signal.throwIfAborted();
			return await withStartupDeadline(fetchSpeechModel(), signal, 'Speech detector did not load in time');
		} catch (error) {
			signal.throwIfAborted();
			logger.warn('SIMD speech detector unavailable; loading the scalar build', error);
		}
	}
	try {
		await loadWorklet(context, signal, vadModules, 'VOICE_VAD_WORKLET_URL');
	} catch (error) {
		signal.throwIfAborted();
		logger.warn('Speech detector unavailable; retaining RMS gate', error);
	}
	return null;
}

export function loadVoiceSoftGain(context: AudioContext, signal: AbortSignal): Promise<void> {
	return loadWorklet(context, signal, gainModules, 'VOICE_SOFT_GAIN_WORKLET_URL');
}

export function createVoiceSoftGain(
	context: AudioContext,
	onFailure: () => void,
	onHealth?: (processingMs: number, audioMs: number, contextTime: number) => void,
	tuning: VoicePipelineTuning = VOICE_PIPELINE_TUNING,
): AudioWorkletNode {
	const node = new AudioWorkletNode(context, 'fluxer-voice-soft-gain', {
		numberOfInputs: 3,
		numberOfOutputs: 1,
		outputChannelCount: [1],
		channelCount: 1,
		channelCountMode: 'explicit',
		processorOptions: {tuning},
	});
	node.port.onmessage = ({data}: MessageEvent) => {
		if (
			data?.type === 'health' &&
			Number.isFinite(data.processingMs) &&
			data.processingMs >= 0 &&
			Number.isFinite(data.audioMs) &&
			data.audioMs > 0 &&
			Number.isFinite(data.contextTime) &&
			data.contextTime >= 0
		) {
			onHealth?.(data.processingMs, data.audioMs, data.contextTime);
		}
	};
	node.onprocessorerror = onFailure;
	return node;
}

export function createVoiceActivityGate(
	context: AudioContext,
	channelCount: 1 | 2,
	config: VoiceActivityGateConfig,
	onLevel: (level: VoiceInputLevel) => void,
	onFailure: () => void,
	linkId?: string,
): AudioWorkletNode {
	const node = new AudioWorkletNode(context, 'fluxer-voice-gate', {
		numberOfInputs: 2,
		numberOfOutputs: 2,
		outputChannelCount: [channelCount, 1],
		channelCount,
		channelCountMode: 'explicit',
		channelInterpretation: 'speakers',
		processorOptions: {
			...config,
			tuning: config.tuning ?? VOICE_PIPELINE_TUNING,
			channelCount,
			minRms: LOCAL_AUTO_MIN_RMS,
			maxRms: LOCAL_MAX_RMS,
			releaseMs: SPEAKING_LOCAL_RELEASE_MS,
			linkId,
		},
	});
	node.port.onmessage = ({data}: MessageEvent<VoiceInputLevel & {type: string}>) => {
		if (data.type === 'level') onLevel(data);
		if (data.type === 'vad-failure') logger.warn('Speech detector failed; retaining RMS gate');
	};
	node.onprocessorerror = onFailure;
	return node;
}
